import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { RequestOptions, Result } from './types';
import { isAxiosError } from 'axios';
import { curry1, err, ok } from './utils';

export type TextMessagingProvider = Pretty<
  Mutable<
    paths['/api/text-messaging-provider']['get']['responses']['200']['content']['application/json'][number]
  >
>;

type TextMessagingProviderRequestData =
  paths['/api/text-messaging-provider']['post']['requestBody']['content']['application/json'];

type CreateTextMessagingProviderBadRequest =
  paths['/api/text-messaging-provider']['post']['responses']['400']['content']['application/json'];

export const createTextMessagingProvider = async (
  instance: AxiosInstance,
  data: TextMessagingProviderRequestData,
  options?: RequestOptions
): Promise<
  Result<TextMessagingProvider, CreateTextMessagingProviderBadRequest>
> => {
  try {
    const res = await instance.post<TextMessagingProvider>(
      '/text-messaging-provider',
      data,
      options
    );

    return ok(res.data);
  } catch (error) {
    if (
      isAxiosError<CreateTextMessagingProviderBadRequest>(error) &&
      error.status === 400 &&
      error.response
    ) {
      return err(error.response.data, error.status);
    }

    throw error;
  }
};

export const deleteTextMessagingProvider = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(`/text-messaging-provider/${id}`, options);
};

export const getTextMessagingProviders = async (
  instance: AxiosInstance,
  options: RequestOptions
): Promise<TextMessagingProvider[]> => {
  const res = await instance.get<TextMessagingProvider[]>(
    '/text-messaging-provider',
    options
  );

  return res.data;
};

export class TextMessagingProviderService {
  public createTextMessagingProvider;
  public deleteTextMessagingProvider;
  public getTextMessagingProviders;

  constructor(private instance: AxiosInstance) {
    this.createTextMessagingProvider = curry1(createTextMessagingProvider)(
      this.instance
    );
    this.deleteTextMessagingProvider = curry1(deleteTextMessagingProvider)(
      this.instance
    );
    this.getTextMessagingProviders = curry1(getTextMessagingProviders)(
      this.instance
    );
  }
}
